import React, { createContext, useContext, useRef } from "react";

const SectionSpacingContext = createContext(null);

const TONE_CLASS = {
  default: "",
  secondary: "bg-secondary/40",
  "secondary-light": "bg-secondary/30",
  cream: "bg-cream/40",
  marquee: "border-y border-border bg-cream/40",
  primary: "bg-primary text-cream",
};

function sectionSpacing(sameTone, variant) {
  if (variant === "hero") return "pt-8 pb-12 sm:pb-24";
  if (variant === "marquee") return "py-5 sm:py-6";
  if (variant === "content") return sameTone ? "pb-10 sm:pb-20" : "pb-10 sm:pb-20";
  if (variant === "cta") {
    return sameTone ? "pt-6 pb-10 sm:pt-10 sm:pb-16 md:pb-24" : "py-14 sm:py-24 md:py-32";
  }
  return sameTone ? "pt-4 pb-10 sm:pt-6 sm:pb-20 md:pb-24" : "py-10 sm:py-20 md:py-24";
}

export function PageShell({ children }) {
  const lastToneRef = useRef(null);
  lastToneRef.current = null;

  const registerSection = (tone) => {
    const sameTone = lastToneRef.current === tone;
    lastToneRef.current = tone;
    return sameTone;
  };

  return (
    <SectionSpacingContext.Provider value={{ registerSection }}>
      <div className="bg-background overflow-x-hidden">{children}</div>
    </SectionSpacingContext.Provider>
  );
}

export function PageSection({ tone = "default", variant = "normal", className = "", children, ...props }) {
  const ctx = useContext(SectionSpacingContext);
  const sameTone = ctx ? ctx.registerSection(tone) : false;
  const spacing = sectionSpacing(sameTone, variant);
  const toneClass = TONE_CLASS[tone] || "";

  return (
    <section className={`px-4 sm:px-6 ${toneClass} ${spacing} ${className}`.replace(/\s+/g, " ").trim()} {...props}>
      {children}
    </section>
  );
}

export function PageHero({ eyebrow, title, highlight, description }) {
  const ctx = useContext(SectionSpacingContext);
  if (ctx) ctx.registerSection("default");

  return (
    <section className="relative px-4 sm:px-6 pt-10 pb-8 sm:pt-12 sm:pb-14 overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,oklch(0.7_0.18_50/0.08),transparent_60%)]" />
      <div className="mx-auto max-w-4xl text-center">
        {eyebrow && (
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-cream/60 px-4 py-1.5 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            {eyebrow}
          </div>
        )}
        <h1 className="mt-6 font-display text-5xl sm:text-6xl md:text-7xl text-balance">
          {title} {highlight && <span className="italic text-accent">{highlight}</span>}
        </h1>
        {description && (
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto text-pretty leading-relaxed">
            {description}
          </p>
        )}
      </div>
    </section>
  );
}
